import { useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import classes from "./styles.module.scss";
import { generatePostsThunk, clearPosts } from "../../store/features/posts/postsSlice";
import { Post } from "../../types/store";
import TextArea from "../../components/Textarea";
import PostCard from "../../components/PostCard";
import SelectGroup from "../../components/SelectGroup";
import Checkbox from "../../components/Checkbox";
import NumberSelector from "../../components/NumberSelector";
import MainButton from "../../components/MainButton";
import Statistics from "./Statistics";
import { showNotification } from "../../store/features/notifications/notificationSlice";
import TraitsCards from "./TraitsCards";
import { useEffect } from "react";
import clsx from "clsx";
import QuickTitle from "./QuickTitle";


export const LandingPage = () => {

  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);


  const dispatch = useAppDispatch();
  const { generatedPosts, loading } = useAppSelector((state: any) => state.posts);

  const resultsRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<HTMLDivElement>(null);

  const handleTryForFree = () => {
    if (appRef.current) {
      const y = appRef.current.getBoundingClientRect().top + window.scrollY - 100; // 100px offset
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }

  const handleGenerate = () => {
    //console.log("Generating posts...");
    dispatch(clearPosts());
    dispatch(
      generatePostsThunk({
        text: mainText,
        posts_number: postsCount,
        tone: postTone,
        hashtags_style: hashChecked ? hashtagsStyle : "",
        emojis: true,
        target_platform: targetPlatform,
        post_length: postLength,
        suggestions: suggestions
      }))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: "Posts generated!", type: "success" }));
      })
      .catch(() => {
        dispatch(showNotification({ message: "Failed to generate posts.", type: "error" }));
      });
    setTimeout(() => {
      if (resultsRef.current) {
        const y = resultsRef.current.getBoundingClientRect().top + window.scrollY - 300; // 100px offset
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 150);
  };

  const [mainText, setMainText] = useState("");
  const [postsCount, setPostsCount] = useState(1);
  const handlePostsNumberChange = (newValue: number) => {
    setPostsCount(newValue);
  };
  const [postTone, setPostTone] = useState("Neutral");
  const [hashChecked, setHashChecked] = useState(false);
  const [hashtagsStyle, setHashtagsStyle] = useState("Minimal");
  const [emojisChecked, setEmojisChecked] = useState(false);
  const [targetPlatform, setTargetPlatform] = useState("General");
  const [postLength, setPostLength] = useState("Medium");
  const [suggestions, setSuggestions] = useState("");


  return (
    <div className={classes.LandingPage}>
      <div ref={ref} className={clsx(
        classes.AppHeader, classes.fadeSection,
        { [classes.isVisible]: visible } // only adds 'visible' if true
      )}>
        <div className={classes.contentWrapper}>
          <QuickTitle text="Join the creators" link="/signin"/>
          <h1 className={classes.title}>Turn your scattered thoughts into scroll-stopping content.</h1>
          <p className={classes.subtitle}>Create engaging, high-quality posts in seconds with the help of AI. Whether you’re building your brand, managing social media, or running marketing campaigns, our AI-powered tool helps you craft content that captures attention, drives engagement, and saves you hours of work.</p>
          <MainButton title="Try for free" icon="chevronDown" onClick={handleTryForFree} />
        </div>
      </div>

      {/* The main free application section */}
      <div ref={appRef} className={classes.AppSection}>
        <div className={classes.contentWrapper}>
          <div className={classes.control}>
            <div className={classes.inputWrapper}>
              <p className={classes.instructionsText}>
                Simplify your content creation process through AI and automation
              </p>
              <TextArea value={mainText} onChange={(e) => setMainText(e.target.value)} maxLength={2000} placeholder="Paste your source text here..." />
              <MainButton title="Create Posts" icon="chevronRight" onClick={handleGenerate} />
            </div>
            <div className={classes.controlsWrapper}>
              <div className={classes.controlsTitle}>
                <h4>Parameters</h4>
                <p>Tweak some settings to tailor your results to your needs.</p>
              </div>
              <div className={classes.mainControls}>
                <div className={classes.settingWrapper}>
                  <h4>How many posts?</h4>
                  <NumberSelector value={postsCount} min={1} max={8} step={1} disabled={false} onChange={handlePostsNumberChange} />
                </div>
                <div className={classes.settingWrapper}>
                  <h4>Tone</h4>
                  <SelectGroup options={["Formal", "Neutral", "Casual", "Fun"]} value={postTone} onChange={setPostTone} />
                </div>
                <div className={classes.settingWrapper}>
                  <Checkbox label="Hashtags" checked={hashChecked} onChange={(e) => setHashChecked(e.target.checked)} />
                  {hashChecked ?
                    <SelectGroup options={["Minimal", "Moderate", "Trending-Heavy", "Gen Z"]} value={hashtagsStyle} onChange={setHashtagsStyle} />
                    :
                    ''}
                </div>
                <div className={classes.settingWrapper}>
                  <Checkbox label="Use Emojis" checked={emojisChecked} onChange={(e) => setEmojisChecked(e.target.checked)} />
                </div>
                <div className={classes.settingWrapper}>
                  <h4>Target a Platform</h4>
                  <SelectGroup options={["General", "Instagram", "TikTok", "LinkedIn", "X", "Facebook"]} value={targetPlatform} onChange={setTargetPlatform} />
                </div>
                <div className={classes.settingWrapper}>
                  <h4>Posts Length</h4>
                  <SelectGroup options={["Very Short", "Short", "Medium", "Long"]} value={postLength} onChange={setPostLength} />
                </div>
                <div className={classes.settingWrapper}>
                  <h4>Suggestions</h4>
                  <TextArea value={suggestions} onChange={(e) => setSuggestions(e.target.value)} maxLength={200} placeholder="Any other tweaks you want to add?" />
                </div>
              </div>
            </div>
          </div>
          <div ref={resultsRef} className={classes.results}>
            {loading && Array.from({ length: postsCount }).map((_, i) => (
              <div key={i} className={classes.skeletonLoader}></div>
            ))}
            {generatedPosts.map((post: Post) => (
              <PostCard isOnDash={false} key={post.post_id} post_id={post.post_id} text={post.caption} hashtags={post.hashtags} image={post.image_url} />
            ))}
          </div>
        </div>
      </div>

      <div className={classes.Statistics}>
        <div className={classes.contentWrapper}>
          <Statistics value="22.22k" description="events captured & processed" />
          <Statistics value="Unlimited" description="personalised user insights and feedback, thanks to Uini's AI-powered technology." />
          <Statistics value="24/7" description="continuous user research and analysis, ensuring you never miss a beat." />
        </div>
      </div>

      <div className={classes.TraitsCards}>
        <div className={classes.contentWrapper}>
          <TraitsCards title="Understand Your Users, Build What They Want" subtitle="Simply install our lightweight widget and start collecting real user insights. Get immediate feedback on your product’s features, validate ideas before building, and ensure you’re always aligned with user needs." />
        </div>
      </div>

      <div className={classes.ShortDescription}>
        <div className={classes.contentWrapper}>
          <div className={classes.logo}>.shortform</div>
          <h2 className={classes.title}>Create scroll-stopping content in seconds</h2>
          <p className={classes.subtitle}>Our AI-powered tool helps you craft content that captures attention, drives engagement, and saves you hours of work.</p>
        </div>
      </div>
    </div>
  )
}

export default LandingPage;